import { apiPost } from '../api/client';
import { ensureInventoryOfflineShell } from './inventoryOfflineContext';

export type ReceiptPreparedItem = { goodsItemId: string; itemName: string; itemVersion: number; baseUnitId: string; baseUnitVersion: number; unitCode: string };
export type ReceiptContext = { userId: string; merchantId: string; warehouseId: string; warehouseName: string; items: ReceiptPreparedItem[] };
export type ReceiptDraft = ReceiptContext & { draftId: string; documentNumber: string; supplierName: string; note: string;
  lines: Array<ReceiptPreparedItem & { quantity: string }>; state: 'EDITING' | 'QUEUED' | 'REJECTED' | 'SYNCED'; documentId?: string; revision?: number };
type Lease = ReceiptContext & { draftId: string; authBinding: string; expiresAt: number };
const key = 'coop.receipt.offline-leases.v1';
const dbName = 'coop-receipt-drafts-v1';
let generation = 0;
export function cancelReceiptOfflinePreparation() { generation++; }
async function tokenIdentity() {
  const token = localStorage.getItem('coop_token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.sub !== 'string' || !Number.isFinite(payload.exp) || payload.exp * 1000 <= Date.now()) return null;
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    if (localStorage.getItem('coop_token') !== token) return null;
    return { userId: payload.sub as string, expiresAt: payload.exp * 1000, binding: Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('') };
  } catch { return null; }
}
export function clearReceiptOfflineContexts(scope?: { userId: string; merchantId: string; warehouseId?: string }) {
  generation++;
  try {
    if (!scope) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify((JSON.parse(sessionStorage.getItem(key) ?? '[]') as Lease[])
      .filter(value => value.userId !== scope.userId || value.merchantId !== scope.merchantId || (scope.warehouseId !== undefined && value.warehouseId !== scope.warehouseId))));
  } catch { try { sessionStorage.removeItem(key); } catch { /* blocked storage must not break authentication */ } }
}
export async function listReceiptOfflineContexts(): Promise<Lease[]> {
  try {
    const identity = await tokenIdentity();
    if (!identity) return [];
    const leases: Lease[] = JSON.parse(sessionStorage.getItem(key) ?? '[]');
    return Array.isArray(leases) ? leases.filter(lease => lease.userId === identity.userId && lease.authBinding === identity.binding
      && lease.expiresAt > Date.now() && lease.expiresAt <= identity.expiresAt && Array.isArray(lease.items)) : [];
  } catch { return []; }
}
async function database() {
  if (typeof indexedDB === 'undefined') throw new Error('Durable storage unavailable');
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(dbName, 1);
    request.onupgradeneeded = () => request.result.createObjectStore('drafts');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
const draftKey = (draft: Pick<ReceiptDraft, 'userId' | 'merchantId' | 'warehouseId' | 'draftId'>) => JSON.stringify([draft.userId, draft.merchantId, draft.warehouseId, draft.draftId]);
async function store<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('drafts', mode);
    const request = operation(tx.objectStore('drafts'));
    tx.oncomplete = () => { db.close(); resolve(request.result); };
    tx.onabort = tx.onerror = () => { db.close(); reject(tx.error ?? request.error ?? new Error('Could not save receipt')); };
  });
}
export async function saveReceiptDraft(draft: ReceiptDraft) {
  const db = await database();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('drafts', 'readwrite');
    const records = tx.objectStore('drafts');
    const request = records.get(draftKey(draft));
    let revision: number;
    let conflict = false;
    tx.oncomplete = () => { db.close(); draft.revision = revision; resolve(); };
    tx.onabort = tx.onerror = () => { db.close(); reject(conflict ? Object.assign(new Error('Receipt changed in another tab; reload before editing'), { code: 'OFFLINE_RECEIPT_LOCAL_CONFLICT' }) : tx.error ?? new Error('Could not save receipt')); };
    request.onsuccess = () => {
      const existing = request.result as ReceiptDraft | undefined;
      const samePayload = !existing || JSON.stringify({ ...existing, state: undefined, documentId: undefined, revision: undefined })
        === JSON.stringify({ ...draft, state: undefined, documentId: undefined, revision: undefined });
      const validState = !existing ? draft.state === 'EDITING' : existing.state === 'EDITING'
        ? ['EDITING', 'QUEUED'].includes(draft.state) : existing.state === 'QUEUED'
          ? ['QUEUED', 'SYNCED', 'REJECTED'].includes(draft.state) && samePayload
          : existing.state === draft.state && samePayload && existing.documentId === draft.documentId;
      if (!validState || (existing && (existing.revision ?? 0) !== (draft.revision ?? 0))) { conflict = true; tx.abort(); return; }
      revision = (existing?.revision ?? 0) + 1;
      records.put({ ...draft, revision }, draftKey(draft));
    };
  });
}
export async function readReceiptDraft(scope: Pick<ReceiptDraft, 'userId' | 'merchantId' | 'warehouseId' | 'draftId'>): Promise<ReceiptDraft | undefined> {
  return store('readonly', store => store.get(draftKey(scope)));
}
export async function prepareReceiptOffline(params: { userId: string; merchantId: string; warehouseId: string; goodsItemIds: string[];
  initial?: { documentNumber: string; supplierName: string; note: string; lines: Array<{ goodsItemId: string; unitCode: string; quantity: string; baseUnitId?: string; baseUnitVersion?: number }> } }) {
  const captured = generation;
  const identity = await tokenIdentity();
  if (!identity || identity.userId !== params.userId || !navigator.onLine) throw new Error('Sign in and prepare this receipt online');
  const context = await apiPost<ReceiptContext>('/goods/receipt-offline/prepare', { merchantId: params.merchantId, warehouseId: params.warehouseId, goodsItemIds: params.goodsItemIds });
  if (context.userId !== params.userId || context.merchantId !== params.merchantId || context.warehouseId !== params.warehouseId) throw new Error('Receipt scope mismatch');
  await ensureInventoryOfflineShell();
  if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt preparation cancelled');
  const draftId = crypto.randomUUID();
  await saveReceiptDraft({ ...context, draftId, documentNumber: params.initial?.documentNumber ?? '', supplierName: params.initial?.supplierName ?? '', note: params.initial?.note ?? '',
    lines: context.items.map(item => { const initial = params.initial?.lines.find(line => line.goodsItemId === item.goodsItemId && line.unitCode === item.unitCode && (line.baseUnitId === item.baseUnitId && line.baseUnitVersion === item.baseUnitVersion));
      return { ...item, quantity: initial?.quantity.replace(',', '.') ?? '' }; }), state: 'EDITING' });
  if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt preparation cancelled');
  const leases = await listReceiptOfflineContexts();
  if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt preparation cancelled');
  sessionStorage.setItem(key, JSON.stringify([...leases, { ...context, draftId, authBinding: identity.binding, expiresAt: Math.min(identity.expiresAt, Date.now() + 3600_000) }].slice(-20)));
  return draftId;
}
export async function receiptDraftAllowed(draft: ReceiptDraft) {
  return (await listReceiptOfflineContexts()).some(lease => lease.draftId === draft.draftId && lease.userId === draft.userId
    && lease.merchantId === draft.merchantId && lease.warehouseId === draft.warehouseId);
}
// Expired/login-rotated leases may be renewed only through fresh server authorization.
// Original queued payloads/keys are retained, including their item/unit versions.
export async function recoverReceiptOffline(offset = 0) {
  const captured = generation;
  const identity = await tokenIdentity();
  if (!identity || !navigator.onLine) throw new Error('Sign in online to recover receipts');
  const drafts = await store<ReceiptDraft[]>('readonly', store => store.getAll());
  const storedLeases = await listReceiptOfflineContexts();
  const leases: Lease[] = [];
  const pending = drafts.filter(value => value.userId === identity.userId && value.state !== 'SYNCED');
  const page = Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;
  for (const draft of pending.slice(page, page + 20)) {
    if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt recovery cancelled');
    try {
      const context = await apiPost<ReceiptContext>('/goods/receipt-offline/prepare', { merchantId: draft.merchantId, warehouseId: draft.warehouseId, goodsItemIds: draft.lines.map(line => line.goodsItemId), reviewUnavailable: true });
      if (context.userId !== draft.userId || context.merchantId !== draft.merchantId || context.warehouseId !== draft.warehouseId) continue;
      if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt recovery cancelled');
      leases.push({ ...context, draftId: draft.draftId,
        authBinding: identity.binding, expiresAt: Math.min(identity.expiresAt, Date.now() + 3600_000) });
    } catch (error) {
      const status = Number((error as { status?: number }).status);
      if (![401, 403, 404, 409].includes(status)) {
        const stored = storedLeases.find(value => value.draftId === draft.draftId && value.userId === draft.userId && value.merchantId === draft.merchantId && value.warehouseId === draft.warehouseId);
        if (stored) leases.push(stored);
      }
      // Warehouse authorization remains mandatory; missing items do not strand an ambiguous replay.
    }
  }
  if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt recovery cancelled');
  await ensureInventoryOfflineShell();
  if (generation !== captured || (await tokenIdentity())?.binding !== identity.binding) throw new Error('Receipt recovery cancelled');
  sessionStorage.setItem(key, JSON.stringify(leases.slice(-20)));
  return { count: leases.length, offset: page, hasMore: page + 20 < pending.length };
}
export async function syncReceiptDraft(draft: ReceiptDraft) {
  const identity = await tokenIdentity();
  if (!identity || identity.userId !== draft.userId || !navigator.onLine || !await receiptDraftAllowed(draft)) throw new Error('Prepare authorized receipt online');
  if (draft.state === 'SYNCED') return draft.documentId;
  if (draft.state === 'REJECTED') throw new Error('Reprepare this rejected receipt online');
  if (draft.lines.some(line => !validReceiptQuantity(line.quantity)) || typeof draft.documentNumber !== 'string'
    || typeof draft.supplierName !== 'string' || typeof draft.note !== 'string'
    || draft.documentNumber.length > 200 || draft.supplierName.length > 500 || draft.note.length > 2000)
    throw Object.assign(new Error('Shorten invoice fields and enter positive quantities within the receipt limit'), { code: 'OFFLINE_RECEIPT_LOCAL_INVALID' });
  // Freeze before the first request; an ambiguous network failure always retries the same body/key.
  draft = { ...draft, state: 'QUEUED' };
  await saveReceiptDraft(draft);
  if ((await tokenIdentity())?.binding !== identity.binding || !await receiptDraftAllowed(draft)) throw new Error('Receipt authentication changed before sync');
  const input = { merchantId: draft.merchantId, warehouseId: draft.warehouseId, draftId: draft.draftId,
    documentNumber: draft.documentNumber, supplierName: draft.supplierName, note: draft.note,
    lines: draft.lines.map(line => ({ goodsItemId: line.goodsItemId, itemVersion: line.itemVersion, baseUnitId: line.baseUnitId,
      baseUnitVersion: line.baseUnitVersion, unitCode: line.unitCode, quantity: line.quantity })) };
  let response: { document: { id: string } };
  try { response = await apiPost('/goods/receipt-offline/sync', input); }
  catch (error) {
    const failure = error as { status?: number; body?: { code?: string } };
    if (failure.status === 409 && ['OFFLINE_RECEIPT_CONTEXT_CHANGED', 'OFFLINE_RECEIPT_ITEM_UNAVAILABLE'].includes(failure.body?.code ?? ''))
      await saveReceiptDraft({ ...draft, state: 'REJECTED' });
    if ([401, 403, 404].includes(Number((error as { status?: number }).status))) clearReceiptOfflineContexts(draft);
    throw error;
  }
  await saveReceiptDraft({ ...draft, state: 'SYNCED', documentId: response.document.id });
  return response.document.id;
}
// Only definitively rejected, never ambiguous, identities may be replaced.
export async function reviewRejectedReceipt(draft: ReceiptDraft) {
  if (draft.state !== 'REJECTED' || !navigator.onLine || !await receiptDraftAllowed(draft)) throw new Error('Recover receipt access online');
  const context = await apiPost<ReceiptContext>('/goods/receipt-offline/prepare', { merchantId: draft.merchantId, warehouseId: draft.warehouseId,
    goodsItemIds: draft.lines.map(line => line.goodsItemId), reviewUnavailable: true });
  if (!await receiptDraftAllowed(draft) || context.userId !== draft.userId || context.merchantId !== draft.merchantId || context.warehouseId !== draft.warehouseId) throw new Error('Receipt access changed');
  return context;
}
export async function reprepareRejectedReceipt(draft: ReceiptDraft, selected: string[]) {
  const context = await reviewRejectedReceipt(draft);
  if (!selected.length || new Set(selected).size !== selected.length || selected.some(id => !context.items.some(item => item.goodsItemId === id))) throw new Error('Select available receipt items');
  return prepareReceiptOffline({ userId: draft.userId, merchantId: draft.merchantId, warehouseId: draft.warehouseId, goodsItemIds: selected,
    initial: { documentNumber: draft.documentNumber, supplierName: draft.supplierName, note: draft.note,
      lines: context.items.flatMap(item => { const original = draft.lines.find(line => line.goodsItemId === item.goodsItemId);
        return original && original.baseUnitId === item.baseUnitId && original.baseUnitVersion === item.baseUnitVersion && original.unitCode === item.unitCode
          ? [{ goodsItemId: item.goodsItemId, unitCode: item.unitCode, quantity: original.quantity, baseUnitId: item.baseUnitId, baseUnitVersion: item.baseUnitVersion }] : []; }) } });
}
export function validReceiptQuantity(quantity: string) {
  if (!/^\d{1,14}(?:\.\d{1,12})?$/.test(quantity) || !/[1-9]/.test(quantity)) return false;
  const [whole, fraction = ''] = quantity.split('.');
  const rounded = BigInt(whole) * 10000n + BigInt(fraction.padEnd(4, '0').slice(0, 4)) + (Number(fraction[4] ?? 0) >= 5 ? 1n : 0n);
  return rounded < 10n ** 18n;
}
